import React from 'react';
import {
  Box, Flex, HStack, Icon, Popover, PopoverContent, PopoverTrigger, Portal, Text,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Link } from 'react-router-dom';
import { getNavMeta } from './navMeta';

/**
 * Top-nav pieces: a plain link item and a hover-opened mega-menu panel. Header.js
 * still owns which nodes exist (the permission-filtered page tree) and which one is
 * active — these only decide how they're drawn.
 *
 * A panel lays a group's direct links out as icon cards (name + one-line
 * description) and turns any nested group (e.g. Settings → User Management) into a
 * titled section of its own, instead of the old hover-flyout submenus.
 */

const brandTint = (alpha) => `rgb(var(--brand-primary-rgb) / ${alpha})`;

const topItemProps = (active) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: 3,
  py: 1.5,
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: active ? '600' : '500',
  color: active ? 'white' : 'gray.700',
  bg: active ? 'var(--brand-primary)' : 'transparent',
  transition: 'background 0.15s, color 0.15s',
  _hover: { bg: active ? 'var(--brand-primary-hover)' : 'gray.100' },
  whiteSpace: 'nowrap',
});

/** A top-level page with no children: icon + name, straight link. */
export const NavTopLink = ({ node, isActive }) => {
  const { icon } = getNavMeta(node);
  return (
    <Box as={Link} to={`/${node.slug}`} {...topItemProps(isActive)} style={{ width: 'auto' }}>
      <Icon as={icon} boxSize="15px" />
      {node.name}
    </Box>
  );
};

/** Leaves under a node, at any depth (deeper levels are flattened into the section). */
const leavesOf = (node) =>
  (node.children || []).flatMap((child) => (child.children?.length ? leavesOf(child) : [child]));

const NavCard = ({ node, isActive, onNavigate }) => {
  const { icon, description } = getNavMeta(node);
  return (
    <Flex
      as={Link}
      to={`/${node.slug}`}
      onClick={onNavigate}
      gap={3}
      align="flex-start"
      p={2.5}
      borderRadius="10px"
      bg={isActive ? brandTint(0.06) : 'transparent'}
      _hover={{ bg: isActive ? brandTint(0.1) : 'gray.50' }}
      transition="background 0.15s"
      style={{ width: '100%' }}
    >
      <Flex
        w="32px" h="32px" flexShrink={0} align="center" justify="center" borderRadius="8px"
        bg={isActive ? 'var(--brand-primary)' : brandTint(0.1)}
        color={isActive ? 'white' : 'var(--brand-primary)'}
      >
        <Icon as={icon} boxSize="16px" />
      </Flex>
      <Box minW={0}>
        <Text fontSize="13px" fontWeight="600" color={isActive ? 'var(--brand-primary)' : 'gray.800'} lineHeight="1.3">
          {node.name}
        </Text>
        {description && (
          <Text fontSize="12px" color="gray.500" lineHeight="1.4" mt={0.5} noOfLines={2}>
            {description}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

// Sizing: the panel asks for enough width for its ideal column count (more links →
// more columns, up to 4), but never more than the window minus a margin. Inside it
// the grids use auto-fill, so when the window is narrower the columns simply wrap —
// adding links later never needs a code change and never overflows the page.
const CARD_MIN_W = 240;
const COLUMN_W = 270;
const VIEWPORT_MARGIN = 16;
const columnsFor = (count) => (count > 12 ? 4 : count > 6 ? 3 : count > 2 ? 2 : 1);
const autoGrid = { display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_W}px, 1fr))` };

const SectionTitle = ({ children }) => (
  <Text fontSize="11px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.06em" px={2.5} mb={1}>
    {children}
  </Text>
);

/**
 * A top-level group: hovering (or focusing) the trigger opens a panel.
 *
 * `nodes` are the items to show — a group's children, or for the "More" overflow
 * the overflowed top-level pages themselves. `header` (optional) is the group node,
 * shown as the panel's title strip.
 */
export const NavMegaMenu = ({ label, header, nodes, isActive, isNavActive, isHidden = () => false }) => {
  const visible = nodes.filter((n) => !isHidden(n));
  const links = visible.filter((n) => !n.children?.length);
  const sections = visible
    .filter((n) => n.children?.length)
    .map((n) => ({ node: n, leaves: leavesOf(n).filter((l) => !isHidden(l)) }))
    .filter((s) => s.leaves.length > 0);

  const cols = Math.min(4, Math.max(columnsFor(links.length), Math.min(sections.length, 4)));
  const headerMeta = header ? getNavMeta(header) : null;

  return (
    <Popover
      trigger="hover"
      placement="bottom"
      openDelay={60}
      closeDelay={120}
      gutter={10}
      isLazy
      // `fixed` keeps the panel out of the page's scroll width (no horizontal
      // scrollbar near the edges); preventOverflow slides it back inside the window.
      strategy="fixed"
      modifiers={[{ name: 'preventOverflow', options: { padding: VIEWPORT_MARGIN, altAxis: true } }]}
    >
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Box as="button" type="button" {...topItemProps(isActive)}>
              {headerMeta && <Icon as={headerMeta.icon} boxSize="15px" />}
              {label}
              <ChevronDownIcon boxSize={4} />
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              w={`min(${cols * COLUMN_W + 24}px, calc(100vw - ${VIEWPORT_MARGIN * 2}px))`}
              maxW="none"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="14px"
              boxShadow="0 12px 32px rgba(16,24,40,0.12), 0 2px 6px rgba(16,24,40,0.06)"
              overflow="hidden"
              zIndex={1400}
              _focus={{ outline: 'none' }}
            >
              {headerMeta && (
                <HStack spacing={3} px={4} py={3} bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
                  <Flex
                    w="28px" h="28px" align="center" justify="center" borderRadius="8px"
                    bg="var(--brand-primary)" color="white" flexShrink={0}
                  >
                    <Icon as={headerMeta.icon} boxSize="14px" />
                  </Flex>
                  <Box minW={0}>
                    <Text fontSize="13px" fontWeight="700" color="gray.800">{header.name}</Text>
                    {headerMeta.description && (
                      <Text fontSize="12px" color="gray.500">{headerMeta.description}</Text>
                    )}
                  </Box>
                </HStack>
              )}
              <Box p={3} maxH="min(70vh, calc(100vh - 160px))" overflowY="auto" overflowX="hidden">
                {links.length > 0 && (
                  <Box {...autoGrid} gap={1}>
                    {links.map((n) => (
                      <NavCard key={n.id} node={n} isActive={isNavActive(n.slug)} onNavigate={onClose} />
                    ))}
                  </Box>
                )}
                {sections.length > 0 && (
                  <Box {...autoGrid} gap={4} mt={links.length ? 3 : 0}
                    pt={links.length ? 3 : 0} borderTop={links.length ? '1px solid' : 'none'} borderColor="gray.100"
                  >
                    {sections.map(({ node, leaves }) => (
                      <Box key={node.id} minW={0}>
                        <SectionTitle>{node.name}</SectionTitle>
                        {leaves.map((l) => (
                          <NavCard key={l.id} node={l} isActive={isNavActive(l.slug)} onNavigate={onClose} />
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
};
